import PropTypes from 'prop-types';
import { Box, Typography, Paper, CircularProgress, Stack, Tooltip, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { TrendingUpRounded, TrendingDownRounded, InfoOutlined, FlagRounded, CheckCircleRounded } from '@mui/icons-material';

const PerformanceCard = ({ metric, data, index }) => {
    const { target, achieved, percentage, remaining, status } = data;
    const isSuccess = status.toLowerCase() === 'green';
    const isWarning = status.toLowerCase() === 'yellow';

    const getStatusColor = () => {
        if (isSuccess) return '#22c55e';
        if (isWarning) return '#f59e0b';
        return '#ef4444';
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ y: -8, transition: { duration: 0.2 } }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: '24px',
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
                {/* Visual Accent */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '40%',
                        height: '100%',
                        background: `linear-gradient(225deg, ${getStatusColor()}10 0%, transparent 100%)`,
                        zIndex: 0
                    }}
                />

                <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography
                                variant="overline"
                                sx={{
                                    fontWeight: 800,
                                    color: 'text.secondary',
                                    letterSpacing: 2,
                                    display: 'block',
                                    lineHeight: 1.2,
                                    minHeight: '2.4em',
                                    overflow: 'hidden'
                                }}
                            >
                                {metric.toUpperCase()}
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 900, color: 'text.primary', mt: 1 }}>
                                <CountUp end={achieved} duration={2} separator="," />
                            </Typography>
                        </Box>
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <CircularProgress variant="determinate" value={100} size={56} thickness={4} sx={{ color: 'grey.100' }} />
                            <CircularProgress
                                variant="determinate"
                                value={percentage > 100 ? 100 : percentage}
                                size={56}
                                thickness={4}
                                sx={{
                                    color: getStatusColor(),
                                    position: 'absolute',
                                    left: 0,
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round'
                                    }
                                }}
                            />
                            <Box
                                sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 900, color: getStatusColor(), fontSize: '0.75rem' }}>
                                    {Math.round(percentage)}%
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>

                    <Stack spacing={2}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                p: 1.5,
                                borderRadius: '16px',
                                bgcolor: 'grey.50/50'
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <FlagRounded sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.7 }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                                    Target
                                </Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={800}>
                                {target.toLocaleString()}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                p: 1.5,
                                borderRadius: '16px',
                                bgcolor: percentage >= 100 ? 'success.50/30' : 'error.50/30'
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                {percentage >= 100 ? (
                                    <CheckCircleRounded sx={{ fontSize: 18, color: '#22c55e' }} />
                                ) : (
                                    <TrendingDownRounded sx={{ fontSize: 18, color: '#ef4444' }} />
                                )}
                                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                                    {percentage >= 100 ? 'Surplus' : 'Remaining'}
                                </Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={800} color={percentage >= 100 ? '#22c55e' : '#ef4444'}>
                                {Math.abs(remaining).toLocaleString()}
                            </Typography>
                        </Box>
                    </Stack>

                    <Box
                        sx={{
                            pt: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        {percentage >= 100 ? (
                            <TrendingUpRounded sx={{ color: '#22c55e', fontSize: 20 }} />
                        ) : (
                            <TrendingDownRounded sx={{ color: getStatusColor(), fontSize: 20 }} />
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 800, color: getStatusColor(), textTransform: 'uppercase' }}>
                            {percentage >= 100 ? 'Exceeding Target' : `${Math.round(100 - percentage)}% to go`}
                        </Typography>
                        <Tooltip title="Real-time performance metrics updated from systems">
                            <IconButton size="small" sx={{ ml: 'auto', opacity: 0.5 }}>
                                <InfoOutlined sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Stack>
            </Paper>
        </motion.div>
    );
};

PerformanceCard.propTypes = {
    metric: PropTypes.string.isRequired,
    data: PropTypes.shape({
        metric: PropTypes.string,
        target: PropTypes.number,
        achieved: PropTypes.number,
        percentage: PropTypes.number,
        remaining: PropTypes.number,
        status: PropTypes.string
    }).isRequired,
    index: PropTypes.number.isRequired
};

export default PerformanceCard;
